import objectHash from "object-hash";
import { useEffect, useLayoutEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { debounceTime, Subject } from "rxjs";
import { selectRefreshToken } from "../../../../store/selectors/currentReportSelectors";
import {
  selectArguments,
  selectReportType,
  selectConditions,
  selectSorts,
  selectValues,
} from "../../../../store/selectors/fieldsStateSelectors";
import { selectDimensions } from "../../../../store/selectors/metaDataSelectors";
import { getAllFilters } from "../../../../utilities/getAllConditions.ts";
import { isPivotConfigurationValid } from "../../../../utilities/isConfigurationValid.ts";
import type { ChartGridState } from "../../../viewer/chart/hooks/gridState.types.ts";
import { generalFieldHash } from "../../pivot/preview/hooks/useGridStateBuilder.ts";
import getTrackedMeasureProperties from "../../utils/getTrackedMeasureProperties.ts";
import useDataLoadingBuilder from "./useDataLoadingBuilder.ts";

export type PreviewChartStateType = ReturnType<typeof useChartStateBuilder>;

export default function useChartStateBuilder(): ChartGridState {
  const subjectDelay = useMemo(() => new Subject<string>(), []);
  const reportType = useSelector(selectReportType) ?? undefined;
  const dimensions = useSelector(selectDimensions);
  const conditionsAreaValues = useSelector(selectConditions);
  const argumentsAreaValues = useSelector(selectArguments);
  const valuesAreaValues = useSelector(selectValues);
  const sortingAreaValues = useSelector(selectSorts);

  const { data, calculating, error, calculate, failedMeasures, handleInvalidConfiguration } = useDataLoadingBuilder();
  const refreshToken = useSelector(selectRefreshToken);

  const configurationValidity = useMemo(() => {
    return isPivotConfigurationValid(
      conditionsAreaValues,
      argumentsAreaValues,
      valuesAreaValues,
      dimensions,
      reportType
    );
  }, [argumentsAreaValues, conditionsAreaValues, dimensions, valuesAreaValues, reportType]);

  const fieldsConfigured = useMemo(
    () => argumentsAreaValues.length > 0 && valuesAreaValues.length > 0,
    [argumentsAreaValues, valuesAreaValues]
  );

  const measuresHash = useMemo(() => objectHash(getTrackedMeasureProperties(valuesAreaValues)), [valuesAreaValues]);

  const conditionsFiltersHash = useMemo(
    () => objectHash(getAllFilters(conditionsAreaValues, valuesAreaValues)),
    [conditionsAreaValues, valuesAreaValues]
  );

  const argumentsAreaHash = useMemo(() => generalFieldHash(argumentsAreaValues), [argumentsAreaValues]);
  const sortingAreaHash = useMemo(() => objectHash(sortingAreaValues), [sortingAreaValues]);

  useLayoutEffect(() => {
    const subscription = subjectDelay.pipe(debounceTime(100)).subscribe(calculate);
    return () => subscription.unsubscribe();
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (configurationValidity.valid && fieldsConfigured) {
      subjectDelay.next(refreshToken);
    } else {
      handleInvalidConfiguration();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [
    conditionsFiltersHash,
    argumentsAreaHash,
    measuresHash,
    refreshToken,
    fieldsConfigured,
    sortingAreaHash,
    configurationValidity.valid,
  ]);

  return { data: data?.result, calculating, error, fieldsConfigured, configurationValidity, failedMeasures };
}
